import { sumBy } from 'lodash-es';
import { Legend } from '../lib/components';
import { GraphWrapper } from '../lib/components/GraphWrapper';
import { ContextProvider } from '../lib/context';
import { useColorAndId, useSortByGroup } from '../lib/hooks/data';
import Lines from './Lines';

/* Example usage:

<LineChart
  data={[
    { name: 'Dec \'17', customer: 3689, all: 453 },
    { name: 'Jan \'18', customer: 6047, all: 573 },
    { name: 'Feb \'18', customer: 6427, all: 547 },
    { name: 'Mar \'18', customer: 6556, all: 640 },
    { name: 'Apr \'18', customer: 5613, all: 568 },
    { name: 'May \'18', customer: 5918, all: 576 },
    { name: 'Jun \'18', customer: 4455, all: 549 },
    { name: 'Jul \'18', customer: 2678, all: 443 },
    { name: 'Aug \'18', customer: 5129, all: 553 },
    { name: 'Sep \'18', customer: 6535, all: 588 },
    { name: 'Oct \'18', customer: 5639, all: 578 },
    { name: 'Nov \'18', customer: 404, all: 68 },
  ]}
  mappings={[
    { name: 'Mynewsdesk', dataKey: 'customer' },
    { name: 'All customers', dataKey: 'all' },
  ]}
/>

*/

interface MappingsProps<T> {
  mappings: {
    name: string;
    dataKey: Extract<keyof Omit<T, 'name'>, string>;
    color?: string;
    groupId?: string;
  }[];
}

type TooltipDataItem = {
  name: string;
} & {
  [profileId: string]:
    | {
        count: number;
        share: number;
      }
    | string; // :(
};
interface BaseProps<T> {
  data: T[];
  tooltipData?: TooltipDataItem[];
  groups?: { id: string }[];
  components?: any;
  totalText?: string;
  height?: number;
  autoSortData?: boolean;
  onClick?: ({
    tick,
    date,
    hits,
    profileIds,
  }: {
    tick: number;
    date: string;
    hits: number;
    profileIds: string[];
  }) => void;
}
type Props<T> = BaseProps<T> & MappingsProps<T>;

function LineChart<T>({
  data,
  tooltipData,
  groups,
  components,
  mappings,
  totalText,
  onClick,
  autoSortData = true,
  height = 320,
}: Props<T>) {
  const sortedMappings = useSortByGroup(mappings, groups);
  const coloredMappings = useColorAndId(autoSortData ? sortedMappings : mappings, groups);

  return (
    <ContextProvider
      minWidth={200}
      height={height}
      components={components}
      selectToggle
      hideSelected
    >
      <GraphWrapper>
        <Legend items={coloredMappings} />
        <Lines
          data={data}
          tooltipData={tooltipData}
          groups={groups}
          mappings={coloredMappings}
          totalText={totalText}
          onClick={
            onClick
              ? ({ tick, filteredMappings }: any) => {
                  const tickData = data[tick] as any;
                  if (!tickData) return;
                  const profileIds: string[] = filteredMappings.map((p: any) => p.dataKey);
                  const hits = sumBy(profileIds, id => tickData[id]);

                  onClick({
                    tick,
                    date: tickData.selectedDate,
                    profileIds,
                    hits,
                  });
                }
              : undefined
          }
        />
      </GraphWrapper>
    </ContextProvider>
  );
}

export default LineChart;
